import React, { useState, useEffect } from 'react';

const AboutUs = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const teamMembers = [
    {
      name: "Dr. Hamza Sher",
      role: "Co-Founder & CTO",
      initials: "SC",
      gradient: "from-blue-500 to-cyan-500",
      description: "Electromagnetic Engineer with 15+ years of experience in EMF research and protection technology",
      expertise: ["EMF Engineering", "Product Development", "Research & Testing"]
    },
    {
      name: "Dr. Muhammad Shayan", 
      role: "Co-Founder & CEO",
      initials: "MR",
      gradient: "from-emerald-500 to-teal-500",
      description: "Public Health Researcher specializing in environmental health and electromagnetic field effects",
      expertise: ["Public Health", "Strategic Planning", "Regulatory Affairs"]
    },
    {
      name: "Emily Johnson",
      role: "Head of Research",
      initials: "EJ", 
      gradient: "from-purple-500 to-pink-500",
      description: "Materials scientist focused on developing innovative EMF shielding materials and technologies",
      expertise: ["Materials Science", "Innovation", "Quality Assurance"]
    }
  ];

  const values = [
    {
      icon: "🔬",
      title: "Science-Based",
      description: "All our products are backed by rigorous scientific research and testing",
      color: "blue",
      delay: "0ms"
    },
    {
      icon: "💚", 
      title: "Health First",
      description: "Your health and well-being are our top priority in everything we do",
      color: "emerald",
      delay: "100ms"
    },
    {
      icon: "🌟",
      title: "Quality",
      description: "We maintain the highest standards of quality in every product we create", 
      color: "amber",
      delay: "200ms"
    },
    {
      icon: "🤝",
      title: "Trust",
      description: "Building long-term relationships based on transparency and reliability",
      color: "rose",
      delay: "300ms"
    }
  ];

  const certifications = [
    { icon: "🏆", title: "FCC Certified", subtitle: "Federal Communications Commission", color: "yellow" },
    { icon: "✓", title: "ISO 9001", subtitle: "Quality Management", color: "blue" },
    { icon: "🌱", title: "RoHS Compliant", subtitle: "Environmental Standards", color: "green" },
    { icon: "⭐", title: "Best Innovation", subtitle: "Health Tech Awards 2023", color: "purple" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Floating particles background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-4 -left-4 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-1/3 -right-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute -bottom-8 left-1/3 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="relative pt-16 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Hero Header */}
          <div 
            id="header"
            data-animate
            className={`text-center mb-20 transition-all duration-1000 ${isVisible.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
              Protecting families since 2018
            </div>
            <h1 className="text-6xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 mb-6">
              About EMF Shop Stream
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
              Leading the way in EMF protection technology, dedicated to safeguarding 
              your health and well-being in our increasingly connected world.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">50K+</div>
                <div className="text-sm text-slate-500">Families Protected</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">7+</div>
                <div className="text-sm text-slate-500">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600">100+</div>
                <div className="text-sm text-slate-500">Products Tested</div>
              </div>
            </div>
          </div>

          {/* Mission Section */}
          <div 
            id="mission"
            data-animate
            className={`mb-20 transition-all duration-1000 delay-200 ${isVisible.mission ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-xl border border-white/20">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-full text-sm font-medium mb-4">
                    🎯 Our Mission
                  </div>
                  <h2 className="text-4xl font-bold text-slate-800 mb-6">Protecting Your Digital Life</h2>
                  <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                    At EMF Shop Stream, we believe everyone deserves to live free from the potential 
                    harmful effects of electromagnetic radiation. Our mission is to provide 
                    scientifically-backed EMF protection solutions that give you and your 
                    family peace of mind in today's technology-driven world.
                  </p>
                  <p className="text-slate-600 leading-relaxed text-lg">
                    We're committed to continuous research, innovation, and education to help 
                    people make informed decisions about EMF exposure and protection.
                  </p>
                  
                  <div className="flex gap-4 mt-8">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Research-Driven
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      Family-First
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      Innovation-Led
                    </div>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="bg-gradient-to-br from-blue-400 via-purple-500 to-indigo-600 rounded-2xl h-80 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-6 flex items-center justify-center">
                          <div className="text-4xl">🛡️</div>
                        </div>
                        <div className="text-2xl font-bold mb-2">Protecting Families Worldwide</div>
                        <div className="text-white/80">Advanced EMF Protection Technology</div>
                      </div>
                    </div>
                    
                    {/* Floating elements */}
                    <div className="absolute top-4 right-4 w-3 h-3 bg-white/30 rounded-full animate-bounce"></div>
                    <div className="absolute bottom-8 left-6 w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{animationDelay: '1s'}}></div>
                    <div className="absolute top-1/3 left-4 w-1 h-1 bg-white/50 rounded-full animate-bounce" style={{animationDelay: '0.5s'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Story Section */}
          <div 
            id="story"
            data-animate
            className={`mb-20 transition-all duration-1000 delay-300 ${isVisible.story ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="bg-gradient-to-r from-white/80 to-blue-50/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-xl border border-white/20">
              <div className="text-center mb-12">
                <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-medium mb-4">
                  📖 Our Story
                </div>
                <h2 className="text-4xl font-bold text-slate-800">The Journey That Started It All</h2>
              </div>
              
              <div className="max-w-4xl mx-auto">
                <div className="relative">
                  {/* Timeline */}
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-400 to-purple-500"></div>
                  
                  <div className="space-y-12">
                    <div className="relative flex items-start gap-6">
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                        2018
                      </div>
                      <div className="flex-1 bg-white/60 rounded-xl p-6 shadow-sm">
                        <h3 className="text-xl font-semibold text-slate-800 mb-2">The Beginning</h3>
                        <p className="text-slate-600">
                          EMF Shop Stream was founded by a team of scientists, engineers, and health 
                          advocates who recognized the growing concern about electromagnetic radiation 
                          exposure in our daily lives.
                        </p>
                      </div>
                    </div>
                    
                    <div className="relative flex items-start gap-6">
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                        <span className="text-2xl">🔬</span>
                      </div>
                      <div className="flex-1 bg-white/60 rounded-xl p-6 shadow-sm">
                        <h3 className="text-xl font-semibold text-slate-800 mb-2">Research & Development</h3>
                        <p className="text-slate-600">
                          Our founders, Dr.Hamza sher and Dr. Muhammad Shayan, combined their expertise 
                          to develop the first generation of EMF Shop Stream protection products in a small laboratory.
                        </p>
                      </div>
                    </div>
                    
                    <div className="relative flex items-start gap-6">
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                        <span className="text-2xl">🌍</span>
                      </div>
                      <div className="flex-1 bg-white/60 rounded-xl p-6 shadow-sm">
                        <h3 className="text-xl font-semibold text-slate-800 mb-2">Global Impact</h3>
                        <p className="text-slate-600">
                          Today,EMF Shop Stream serves over 50,000 families worldwide, maintaining our 
                          commitment to quality, science, and customer care through rigorous testing 
                          and continuous innovation.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Values Section */}
          <div 
            id="values"
            data-animate
            className={`mb-20 transition-all duration-1000 delay-400 ${isVisible.values ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-green-100 to-blue-100 text-green-700 rounded-full text-sm font-medium mb-4">
                ⭐ Our Values
              </div>
              <h2 className="text-4xl font-bold text-slate-800">What Drives Us Forward</h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <div 
                  key={index}
                  className="group bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 hover:bg-white/90"
                  style={{animationDelay: value.delay}}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br from-${value.color}-400 to-${value.color}-600 rounded-xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <div className="text-white text-2xl">{value.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3 text-center">{value.title}</h3>
                  <p className="text-slate-600 text-sm text-center leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Team Section */}
          <div 
            id="team"
            data-animate
            className={`mb-20 transition-all duration-1000 delay-500 ${isVisible.team ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 rounded-full text-sm font-medium mb-4">
                👥 Our Team
              </div>
              <h2 className="text-4xl font-bold text-slate-800">Meet the Innovators</h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => (
                <div 
                  key={index}
                  className="group bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 text-center"
                >
                  <div className={`w-24 h-24 bg-gradient-to-br ${member.gradient} rounded-full mx-auto mb-6 flex items-center justify-center text-white text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {member.initials}
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-1">{member.name}</h3>
                  <p className="text-blue-600 font-semibold mb-4">{member.role}</p>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                    {member.description}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {member.expertise.map((skill, skillIndex) => (
                      <span key={skillIndex} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div 
            id="certifications"
            data-animate
            className={`mb-20 transition-all duration-1000 delay-600 ${isVisible.certifications ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-xl border border-white/20">
              <div className="text-center mb-12">
                <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-700 rounded-full text-sm font-medium mb-4">
                  🏅 Certifications & Recognition
                </div>
                <h2 className="text-4xl font-bold text-slate-800">Trusted & Certified</h2>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {certifications.map((cert, index) => (
                  <div 
                    key={index}
                    className="group text-center p-6 border-2 border-slate-200 rounded-xl hover:border-blue-300 hover:shadow-lg transition-all duration-300 bg-white/50"
                  >
                    <div className={`w-16 h-16 bg-${cert.color}-400 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                      <div className="text-white text-2xl">{cert.icon}</div>
                    </div>
                    <h3 className="font-bold text-slate-800 mb-1">{cert.title}</h3>
                    <p className="text-slate-500 text-sm">{cert.subtitle}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div 
            id="cta"
            data-animate
            className={`transition-all duration-1000 delay-700 ${isVisible.cta ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-center text-white overflow-hidden">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-full h-full" style={{
                  backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)`,
                  backgroundSize: '50px 50px'
                }}></div>
              </div>
              
              <div className="relative z-10">
                <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
                  <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
                  Join our growing community
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">Join the EMF Shop Stream Family</h2>
                <p className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed opacity-90">
                  Experience the peace of mind that comes with scientifically-proven EMF protection. 
                  Join over 50,000 families who trust EMF Shop Stream for their health and safety.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                  <button className="group bg-white text-blue-600 px-8 py-4 rounded-full font-bold hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-2">
                    <span>Shop Protection Products</span>
                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center group-hover:translate-x-1 transition-transform duration-300">
                      <span className="text-white text-xs">→</span>
                    </div>
                  </button>
                  <button className="group border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-blue-600 transition-all duration-300 hover:-translate-y-1">
                    Learn More About EMF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;